import { Box, styled, Tooltip } from '@mui/material';
import classNames from 'classnames';

import { AircraftEvent, TrialBadge } from '#entities';
import { EAircraftEventType, EAircraftStatus, getOpacityHex } from '#shared';

import { useAircraftDrawer } from './AircraftDrawerContext';
import { EventTime } from './EventTime';

const PREFIX = 'TSMED-AircraftDrawerAircraftEvents';

export const aircraftDrawerAircraftEventsClasses = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  headerIcon: `${PREFIX}-headerIcon`,
  headerRegistration: `${PREFIX}-headerRegistration`,
  status: `${PREFIX}-status`,
  aircraftEvents: `${PREFIX}-aircraftEvents`,
  aircraftEvent: `${PREFIX}-aircraftEvent`,
  aircraftEventLabel: `${PREFIX}-aircraftEventLabel`,
  aircraftEventTime: `${PREFIX}-aircraftEventTime`,
};

const Root = styled(Box)(({ theme: { spacing, palette, mixins, utils } }) => ({
  ...mixins.organStyles(palette),
  ...mixins.severityStyles(palette),
  ...mixins.aircraftStatusStyles(palette),
  fontSize: utils.clamp(14, 20),
  display: 'flex',
  flexDirection: 'column',
  gap: spacing(1),
  [`.${aircraftDrawerAircraftEventsClasses.aircraftEvent}`]: {
    display: 'flex',
    gap: spacing(1),
    color: 'white',
    [`.${aircraftDrawerAircraftEventsClasses.aircraftEventLabel},.${aircraftDrawerAircraftEventsClasses.aircraftEventTime}`]:
      {
        whiteSpace: 'nowrap',
        lineHeight: utils.clamp(18, 32),
        padding: spacing(0.5, 1),
        color: `var(--organColor, var(--aircraftStatusMaintenanceColor, ${palette.grey[100]}) )`,
        background: `var(--organSecondaryColor, var(--aircraftStatusMaintenanceSecondaryColor,  ${palette.grey[100]}${getOpacityHex(0.15)}))`,
        ...mixins.organStyles(palette, { prefix: `&` }),
      },
    [`.${aircraftDrawerAircraftEventsClasses.aircraftEventLabel}`]: {
      display: 'flex',
      gap: spacing(1),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`.${aircraftDrawerAircraftEventsClasses.aircraftEventTime}`]: {
      textAlign: 'start',
      sup: {
        lineHeight: 0,
        fontSize: utils.clamp(12, 16),
        width: 0,
        display: 'inline-block',
      },
    },
  },
}));

export const AircraftDrawerAircraftEvents = () => {
  const {
    aircraftData: { aircraftEvents },
  } = useAircraftDrawer();
  return (
    <Root>
      {aircraftEvents
        .reduce<AircraftEvent[]>((acc, e) => {
          if (e.type === EAircraftEventType.Trip && e.children) {
            acc.push(...e.children.filter(l => l.range[1] > Date.now()));
          } else {
            acc.push(e);
          }
          return acc;
        }, [])
        .toSorted((a, b) => a.range[0] - b.range[0])
        .map(event => (
          <Box
            key={event.id}
            className={classNames(
              aircraftDrawerAircraftEventsClasses.aircraftEvent,
            )}
          >
            <Tooltip title={event.label} arrow>
              <Box
                className={classNames(
                  aircraftDrawerAircraftEventsClasses.aircraftEventLabel,
                  `event-type-${event.type}`,

                  {
                    [`aircraft-status-${EAircraftStatus.Maintenance}`]:
                      event.type === EAircraftEventType.Maintenance,
                    [`maintenance-type-${event.maintenanceType?.OpsBoardBadge}`]:
                      !!event.maintenanceType?.OpsBoardBadge,
                    [`organ-type-${event.organ}`]: event.organ,
                  },
                )}
              >
                {event.label}
                {event.hasTrial && <TrialBadge size={12} />}
              </Box>
            </Tooltip>
            <Box
              className={classNames(
                aircraftDrawerAircraftEventsClasses.aircraftEventTime,
                `event-type-${event.type}`,
                `maintenance-type-${event.maintenanceType?.OpsBoardBadge}`,
                { [`organ-type-${event.organ}`]: event.organ },
              )}
            >
              <EventTime
                startUnix={event.startUnix}
                startIanaTimezone={event.startIanaTimezone}
                endUnix={event.endUnix}
                endIanaTimezone={event.endIanaTimezone}
                sectorIndex={event.sectorIndex}
              />
            </Box>
          </Box>
        ))}
    </Root>
  );
};
